import React from "react";
import { Card, Button, Typography, Col, Row, Modal, Table, Spin } from "antd";
import { DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import { theme } from "../constants/theme";
import {
  formatNumber,
  getCantonPercentage,
  getParishPercentage,
} from "../utils/formatters";
import { utils, writeFile } from "xlsx";
import { getParishVehicles } from "../../../api/salesLocationApi";

const SummaryTables = ({
  cantonStats,
  parishStats,
  parishDealerStats,
  pichinchaTotal,
  guayasTotal,
  handleDownloadCantones,
  handleDownloadParishDealers,
}) => {
  const [modalVisible, setModalVisible] = React.useState(false);
  const [modalTitle, setModalTitle] = React.useState("");
  const [vehicleData, setVehicleData] = React.useState([]);
  const [loadingVehicles, setLoadingVehicles] = React.useState(false);
  const [selectedParish, setSelectedParish] = React.useState({});
  const [timeType, setTimeType] = React.useState(""); // diurno o nocturno

  // Estados para los filtros de puntos de venta
  const [dealerFilterDiurnoPichincha, setDealerFilterDiurnoPichincha] =
    React.useState("");
  const [dealerFilterNocturnoPichincha, setDealerFilterNocturnoPichincha] =
    React.useState("");
  const [dealerFilterDiurnoGuayas, setDealerFilterDiurnoGuayas] =
    React.useState("");
  const [dealerFilterNocturnoGuayas, setDealerFilterNocturnoGuayas] =
    React.useState("");

  // Función para obtener los vehículos de una parroquia
  const fetchParishVehicles = async (
    province,
    canton,
    parish,
    timeOfDay,
    selectedDealer = null
  ) => {
    setLoadingVehicles(true);
    try {
      // Usar la función del API
      const response = await getParishVehicles(
        province,
        canton,
        parish,
        timeOfDay,
        selectedDealer
      );

      // Si hay dealer seleccionado, filtramos los resultados
      if (selectedDealer && response.data) {
        setVehicleData(
          response.data.filter((vehicle) => vehicle.dealer === selectedDealer)
        );
      } else {
        setVehicleData(response.data || []);
      }
    } catch (error) {
      console.error("Error al obtener los vehículos:", error);
      setVehicleData([]);
    } finally {
      setLoadingVehicles(false);
    }
  };

  // Función para mostrar el modal con la información de los vehículos
  const showVehicleModal = (
    province,
    canton,
    parish,
    parishObj,
    type,
    selectedDealer = null,
    dealerFilter = null
  ) => {
    // Usamos el mismo tipo en lugar del opuesto para mostrar los vehículos correctos
    // const oppositeType = type === "diurno" ? "nocturno" : "diurno";
    const currentType = type;
    const currentTypeLabel =
      currentType === "diurno" ? "TRABAJO (DIURNO)" : "DOMICILIO (NOCTURNO)";

    const title = selectedDealer
      ? `Vehículos de ${parish} - ${province} (${currentTypeLabel}) - ${selectedDealer}`
      : `Vehículos de ${parish} - ${province} (${currentTypeLabel})`;

    setSelectedParish({
      province,
      canton,
      parish,
      type,
      dealer: selectedDealer, // Guardar el dealer seleccionado
    });

    setTimeType(currentType);
    setModalTitle(title);
    fetchParishVehicles(province, canton, parish, currentType, selectedDealer);
    setModalVisible(true);
    setDealerFilterDiurnoPichincha(dealerFilter);
  };

  // Función para exportar a Excel los vehículos en el modal
  const handleExportVehiclesToExcel = () => {
    if (!vehicleData.length) return;

    // Crear los datos para Excel
    const data = vehicleData.map((vehicle) => ({
      VIN: vehicle.vin || "N/A",
      Marca: vehicle.brand || "No disponible",
      [`Parroquia (${
        selectedParish.type === "diurno" ? "Diurno" : "Nocturno"
      })`]:
        selectedParish.type === "diurno"
          ? vehicle.day_parish
          : vehicle.night_parish || "No asignada",
      [`Distancia (${
        selectedParish.type === "diurno" ? "Diurno" : "Nocturno"
      })`]: vehicle.day_distance
        ? `${parseFloat(vehicle.day_distance).toFixed(2)} KM`
        : "N/A",
      [`Parroquia (${
        selectedParish.type === "diurno" ? "Nocturno" : "Diurno"
      })`]:
        selectedParish.type === "diurno"
          ? vehicle.night_parish
          : vehicle.day_parish || "No asignada",
      [`Distancia (${
        selectedParish.type === "diurno" ? "Nocturno" : "Diurno"
      })`]: vehicle.night_distance
        ? `${parseFloat(vehicle.night_distance).toFixed(2)} KM`
        : "N/A",
      "Punto de Venta": vehicle.dealer || "No asignado",
    }));

    // Crear y descargar el archivo Excel
    const workbook = utils.book_new();
    const worksheet = utils.json_to_sheet(data);
    utils.book_append_sheet(workbook, worksheet, "Vehículos");

    const fileName = selectedParish.dealer
      ? `Vehículos_${selectedParish.parish}_${selectedParish.province}_${
          selectedParish.type
        }_${selectedParish.dealer.replace(/\s+/g, "_")}.xlsx`
      : `Vehículos_${selectedParish.parish}_${selectedParish.province}_${selectedParish.type}.xlsx`;

    try {
      writeFile(workbook, fileName);
      // Mostrar mensaje de éxito usando antd message (requiere importar Message)
      console.log("Excel exportado correctamente");
    } catch (error) {
      console.error("Error al exportar el archivo Excel:", error);
    }
  };

  // Botón para visualizar vehículos
  const viewButton = (onClick) => (
    <Button
      type="primary"
      size="small"
      icon={<EyeOutlined />}
      onClick={onClick}
      style={{
        backgroundColor: theme.colors.primary,
        borderColor: theme.colors.primary,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "24px",
        height: "24px",
        minWidth: "24px",
        padding: 0,
        marginRight: "8px",
      }}
    />
  );

  const exportButton = (onClick) => (
    <Button
      type="primary"
      size="small"
      icon={<DownloadOutlined />}
      onClick={onClick}
      style={{
        backgroundColor: theme.colors.success,
        borderColor: theme.colors.success,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "32px",
        height: "32px",
        minWidth: "32px",
        padding: 0,
      }}
    />
  );

  // Columnas para la tabla del modal
  const vehicleColumns = [
    {
      title: "VIN",
      dataIndex: "vin",
      key: "vin",
      render: (text) => text || "N/A",
    },
    {
      title: "Marca",
      dataIndex: "brand",
      key: "brand",
      render: (text) => text || "No disponible",
    },
    {
      title:
        selectedParish.type === "diurno"
          ? "Parroquia (Diurno)"
          : "Parroquia (Nocturno)",
      dataIndex:
        selectedParish.type === "diurno" ? "day_parish" : "night_parish",
      key: "parish",
      render: (text) => text || "No asignada",
    },
    {
      title:
        selectedParish.type === "diurno"
          ? "Dist. Prom. (KM)"
          : "Dist. Prom. (KM)",
      dataIndex: "day_distance",
      key: "day_distance",
      render: (text, record) =>
        record.day_distance
          ? `${parseFloat(record.day_distance).toFixed(2)} KM`
          : "N/A",
    },
    {
      title:
        selectedParish.type === "diurno"
          ? "Parroquia (Nocturno)"
          : "Parroquia (Diurno)",
      dataIndex:
        selectedParish.type === "diurno" ? "night_parish" : "day_parish",
      key: "opposite_parish",
      render: (text) => text || "No asignada",
    },
    {
      title:
        selectedParish.type === "diurno"
          ? "Dist. Prom. (KM)"
          : "Dist. Prom. (KM)",
      dataIndex: "night_distance",
      key: "night_distance",
      render: (text, record) =>
        record.night_distance
          ? `${parseFloat(record.night_distance).toFixed(2)} KM`
          : "N/A",
    },
    {
      title: "Punto de Venta",
      dataIndex: "dealer",
      key: "dealer",
      render: (text) => text || "No asignado",
    },
  ];

  const titleWithButton = (title, onDownload) => (
    <div
      style={{
        padding: "0 8px",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <Typography.Title
        level={5}
        style={{
          margin: 0,
          textAlign: "center",
          width: "100%",
          fontSize: "16px",
        }}
      >
        {title}
      </Typography.Title>
      <div style={{ marginLeft: "8px" }}>{exportButton(onDownload)}</div>
    </div>
  );

  const handleDownloadParishes = (province) => {
    const data = [];
    const provinceCantons = cantonStats[province];
    const provinceParishStats = parishStats[province];

    provinceCantons.forEach((canton) => {
      // Ya no agregamos la fila del cantón

      // Agregar filas de parroquias del cantón
      const parishList = provinceParishStats[canton.day_canton] || [];
      parishList.forEach((parish) => {
        data.push({
          Parroquia: parish.day_parish,
          "Cantidad Vehículos": parish.total_vehicles,
          Porcentaje: `${getParishPercentage(
            parish.total_vehicles,
            canton.total_vehicles
          )}%`,
        });
      });
    });

    const workbook = utils.book_new();
    const worksheet = utils.json_to_sheet(data);
    utils.book_append_sheet(workbook, worksheet, "Parroquias");
    writeFile(workbook, `Resumen_Parroquias_${province}.xlsx`);
  };

  const DealerChart = ({ dealers = [], selectedDealer = "" }) => {
    // Validación básica
    if (!Array.isArray(dealers) || dealers.length === 0) {
      return (
        <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
          No hay datos disponibles
        </div>
      );
    }

    // Procesar datos para la visualización
    let validDealers = dealers
      .filter(
        (dealer) =>
          dealer &&
          dealer.name &&
          dealer.vehicles !== undefined &&
          dealer.percentage !== undefined
      )
      .map((dealer) => ({
        name: dealer.name,
        vehicles: parseInt(dealer.vehicles) || 0,
        percentage: parseFloat(dealer.percentage) || 0,
        // Si existe distancia promedio, la usamos, de lo contrario N/A
        avgDistance:
          dealer.avgDistance !== undefined
            ? parseFloat(dealer.avgDistance).toFixed(2)
            : "N/A",
      }))
      .sort((a, b) => b.vehicles - a.vehicles);

    // Aplicar filtro por concesionario si está seleccionado
    if (selectedDealer) {
      validDealers = validDealers.filter(
        (dealer) => dealer.name === selectedDealer
      );
    }

    // Si no hay datos válidos
    if (validDealers.length === 0) {
      return (
        <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
          No hay datos válidos para mostrar
        </div>
      );
    }

    // Mostrar datos en una tabla simple
    return (
      <div style={{ height: "180px", overflowY: "auto" }}>
        <table
          style={{
            width: "100%",
            fontSize: "13px",
            borderCollapse: "collapse",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  textAlign: "left",
                  padding: "6px",
                  borderBottom: "1px solid #eee",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                Punto de Venta
              </th>
              <th
                style={{
                  textAlign: "right",
                  padding: "6px",
                  borderBottom: "1px solid #eee",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                Vehículos
              </th>
              <th
                style={{
                  textAlign: "right",
                  padding: "6px",
                  borderBottom: "1px solid #eee",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                %
              </th>
              <th
                style={{
                  textAlign: "right",
                  padding: "6px",
                  borderBottom: "1px solid #eee",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                Dist. Prom. (km)
              </th>
            </tr>
          </thead>
          <tbody>
            {validDealers.map((dealer, index) => (
              <tr key={`${dealer.name}-${index}`}>
                <td
                  style={{
                    textAlign: "left",
                    padding: "6px",
                    borderBottom: "1px solid #f5f5f5",
                    color: theme.colors.colors10
                      ? theme.colors.colors10[index % 6]
                      : "#333",
                    fontSize: "13px",
                  }}
                >
                  {dealer.name}
                </td>
                <td
                  style={{
                    textAlign: "right",
                    padding: "6px",
                    borderBottom: "1px solid #f5f5f5",
                    fontWeight: "500",
                    fontSize: "13px",
                  }}
                >
                  {dealer.vehicles}
                </td>
                <td
                  style={{
                    textAlign: "right",
                    padding: "6px",
                    borderBottom: "1px solid #f5f5f5",
                    fontSize: "13px",
                  }}
                >
                  {dealer.percentage.toFixed(1)}%
                </td>
                <td
                  style={{
                    textAlign: "right",
                    padding: "6px",
                    borderBottom: "1px solid #f5f5f5",
                    fontSize: "13px",
                  }}
                >
                  {dealer.avgDistance} KM
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Función para obtener la lista única de concesionarios
  const getUniqueDealers = (dealerStats, province) => {
    if (!dealerStats) return [];

    const dealerSet = new Set();

    Object.entries(dealerStats)
      .filter(([parish]) => parish.includes(province))
      .forEach(([_, data]) => {
        data.dealers.forEach((dealer) => {
          dealerSet.add(dealer.name);
        });
      });

    return Array.from(dealerSet).sort();
  };

  // Componente para el selector de punto de venta
  const DealerSelector = ({ dealers, value, onChange, placeholder }) => {
    return (
      <div style={{ padding: "0 16px 16px 16px" }}>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: "100%",
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #d9d9d9",
            fontSize: "14px",
          }}
        >
          <option value="">{placeholder || "Todos los puntos de venta"}</option>
          {dealers.map((dealer) => (
            <option key={dealer} value={dealer}>
              {dealer}
            </option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <>
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={8}>
          <Card
            title={titleWithButton(
              "RESUMEN DE VEHÍCULOS POR CANTONES",
              handleDownloadCantones
            )}
            style={{
              ...theme.cards.base,
              height: "600px",
              display: "flex",
              flexDirection: "column",
            }}
            bodyStyle={{ padding: "16px", flex: 1, overflow: "hidden" }}
          >
            <div style={{ height: "100%", overflowY: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  tableLayout: "fixed",
                }}
              >
                <thead>
                  <tr>
                    <th style={{ ...theme.table.header, width: "40%" }}>
                      CANTÓN
                    </th>
                    <th style={{ ...theme.table.header, width: "35%" }}>
                      CANTIDAD
                    </th>
                    <th style={{ ...theme.table.header, width: "25%" }}>%</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Pichincha y sus cantones */}
                  <tr>
                    <td
                      style={{
                        ...theme.table.cell,
                        fontWeight: "600",
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      PICHINCHA
                    </td>
                    <td
                      style={{
                        ...theme.table.cell,
                        fontWeight: "600",
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      {formatNumber(pichinchaTotal)}
                    </td>
                    <td
                      style={{
                        ...theme.table.cell,
                        fontWeight: "600",
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      100%
                    </td>
                  </tr>
                  {cantonStats.Pichincha.map((canton) => (
                    <tr key={canton.day_canton}>
                      <td style={theme.table.cell}>{canton.day_canton}</td>
                      <td style={theme.table.cell}>
                        {formatNumber(canton.total_vehicles)}
                      </td>
                      <td style={theme.table.cell}>
                        {getCantonPercentage(canton, pichinchaTotal)}%
                      </td>
                    </tr>
                  ))}
                  {/* Guayas y sus cantones */}
                  <tr>
                    <td
                      style={{
                        ...theme.table.cell,
                        fontWeight: "600",
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      GUAYAS
                    </td>
                    <td
                      style={{
                        ...theme.table.cell,
                        fontWeight: "600",
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      {formatNumber(guayasTotal)}
                    </td>
                    <td
                      style={{
                        ...theme.table.cell,
                        fontWeight: "600",
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      100%
                    </td>
                  </tr>
                  {cantonStats.Guayas.map((canton) => (
                    <tr key={canton.day_canton}>
                      <td style={theme.table.cell}>{canton.day_canton}</td>
                      <td style={theme.table.cell}>
                        {formatNumber(canton.total_vehicles)}
                      </td>
                      <td style={theme.table.cell}>
                        {getCantonPercentage(canton, guayasTotal)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card
            title={titleWithButton(
              "RESUMEN DE VEHÍCULOS POR PARROQUIAS - PICHINCHA",
              () => handleDownloadParishes("Pichincha")
            )}
            style={{
              ...theme.cards.base,
              height: "600px",
              display: "flex",
              flexDirection: "column",
            }}
            bodyStyle={{ padding: "16px", flex: 1, overflow: "hidden" }}
          >
            <div style={{ height: "100%", overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...theme.table.header, width: "40%" }}>
                      PARROQUIA
                    </th>
                    <th style={{ ...theme.table.header, width: "25%" }}>
                      CANTIDAD
                    </th>
                    <th style={{ ...theme.table.header, width: "20%" }}>%</th>
                    <th style={{ ...theme.table.header, width: "15%" }}>VER</th>
                  </tr>
                </thead>
                <tbody>
                  {cantonStats.Pichincha.map((canton) => {
                    const parishList =
                      parishStats.Pichincha[canton.day_canton] || [];
                    return (
                      <React.Fragment key={canton.day_canton}>
                        <tr>
                          <td
                            style={{
                              ...theme.table.cell,
                              fontWeight: "600",
                              backgroundColor: "#f5f5f5",
                            }}
                            colSpan="4"
                          >
                            {canton.day_canton}
                          </td>
                        </tr>
                        {parishList.map((parish) => (
                          <tr key={`${canton.day_canton}-${parish.day_parish}`}>
                            <td style={theme.table.cell}>
                              {parish.day_parish}
                            </td>
                            <td style={theme.table.cell}>
                              {formatNumber(parish.total_vehicles)}
                            </td>
                            <td style={theme.table.cell}>
                              {getParishPercentage(
                                parish.total_vehicles,
                                canton.total_vehicles
                              )}
                              %
                            </td>
                            <td
                              style={{
                                ...theme.table.cell,
                                textAlign: "center",
                              }}
                            >
                              {viewButton(() =>
                                showVehicleModal(
                                  "Pichincha",
                                  canton.day_canton,
                                  parish.day_parish,
                                  parish,
                                  "diurno",
                                  dealerFilterDiurnoPichincha || null
                                )
                              )}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card
            title={titleWithButton(
              "RESUMEN DE VEHÍCULOS POR PARROQUIAS - GUAYAS",
              () => handleDownloadParishes("Guayas")
            )}
            style={{
              ...theme.cards.base,
              height: "600px",
              display: "flex",
              flexDirection: "column",
            }}
            bodyStyle={{ padding: "16px", flex: 1, overflow: "hidden" }}
          >
            <div style={{ height: "100%", overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...theme.table.header, width: "40%" }}>
                      PARROQUIA
                    </th>
                    <th style={{ ...theme.table.header, width: "25%" }}>
                      CANTIDAD
                    </th>
                    <th style={{ ...theme.table.header, width: "20%" }}>%</th>
                    <th style={{ ...theme.table.header, width: "15%" }}>VER</th>
                  </tr>
                </thead>
                <tbody>
                  {cantonStats.Guayas.map((canton) => {
                    const parishList =
                      parishStats.Guayas[canton.day_canton] || [];
                    return (
                      <React.Fragment key={canton.day_canton}>
                        <tr>
                          <td
                            style={{
                              ...theme.table.cell,
                              fontWeight: "600",
                              backgroundColor: "#f5f5f5",
                            }}
                            colSpan="4"
                          >
                            {canton.day_canton}
                          </td>
                        </tr>
                        {parishList.map((parish) => (
                          <tr key={`${canton.day_canton}-${parish.day_parish}`}>
                            <td style={theme.table.cell}>
                              {parish.day_parish}
                            </td>
                            <td style={theme.table.cell}>
                              {formatNumber(parish.total_vehicles)}
                            </td>
                            <td style={theme.table.cell}>
                              {getParishPercentage(
                                parish.total_vehicles,
                                canton.total_vehicles
                              )}
                              %
                            </td>
                            <td
                              style={{
                                ...theme.table.cell,
                                textAlign: "center",
                              }}
                            >
                              {viewButton(() =>
                                showVehicleModal(
                                  "Guayas",
                                  canton.day_canton,
                                  parish.day_parish,
                                  parish,
                                  "diurno",
                                  dealerFilterDiurnoGuayas || null
                                )
                              )}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Listados adicionales de resumen por parroquia vs concesionario */}
      <Row gutter={[24, 24]} style={{ marginTop: "24px" }}>
        <Col span={24}>
          <Card
            style={{
              ...theme.cards.base,
              padding: 0,
              marginBottom: "24px",
            }}
            bodyStyle={{ padding: 0 }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "10px 24px",
                height: "64px",
              }}
            >
              <Typography.Title
                level={4}
                style={{
                  margin: 0,
                  color: theme.colors.text,
                  fontSize: "16px",
                  textAlign: "center",
                  width: "100%",
                }}
              >
                RESUMEN DE CONCENTRACIÓN DE VEHÍCULOS POR PARROQUIA VS PUNTO DE
                VENTA
              </Typography.Title>
            </div>
          </Card>
        </Col>
        <Col span={12}>
          <Card
            title={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <span
                  style={{ fontSize: "14px", textAlign: "center", flex: 1 }}
                >
                  PARROQUIAS DE PICHINCHA – LUGARES DE TRABAJO (DIURNO)
                </span>
                {exportButton(() =>
                  handleDownloadParishDealers(
                    "Pichincha",
                    "diurno",
                    dealerFilterDiurnoPichincha || null
                  )
                )}
              </div>
            }
            style={{
              ...theme.cards.base,
              height: "600px",
              display: "flex",
              flexDirection: "column",
            }}
            bodyStyle={{ padding: "16px", flex: 1, overflow: "hidden" }}
          >
            <DealerSelector
              dealers={getUniqueDealers(parishDealerStats.diurno, "Pichincha")}
              value={dealerFilterDiurnoPichincha}
              onChange={setDealerFilterDiurnoPichincha}
              placeholder="Filtrar por punto de venta"
            />
            <div style={{ height: "100%", overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...theme.table.header, width: "30%" }}>
                      PARROQUIA
                    </th>
                    <th style={{ ...theme.table.header, width: "15%" }}>
                      DIST. PROM. (KM)
                    </th>
                    <th style={{ ...theme.table.header, width: "55%" }}>
                      DISTRIBUCIÓN DE PUNTOS DE VENTA
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {parishDealerStats.diurno &&
                    Object.entries(parishDealerStats.diurno)
                      .filter(([parish]) => parish.includes("Pichincha"))
                      .filter(
                        ([_, data]) =>
                          // Si hay un filtro de dealer, verificar que al menos uno de los dealers coincida
                          !dealerFilterDiurnoPichincha ||
                          data.dealers.some(
                            (dealer) =>
                              dealer.name === dealerFilterDiurnoPichincha
                          )
                      )
                      .map(([parish, data]) => {
                        const [, parishName] = parish.split(" - ");
                        // Obtener el cantón al que pertenece esta parroquia
                        const cantonInfo = Object.entries(
                          parishStats.Pichincha
                        ).find(([canton, parishes]) =>
                          parishes.some((p) => p.day_parish === parishName)
                        );
                        const canton = cantonInfo ? cantonInfo[0] : "";

                        // Calcular la distancia promedio de los concesionarios
                        let calculatedDealers = data.dealers;
                        if (dealerFilterDiurnoPichincha) {
                          calculatedDealers = data.dealers.filter(
                            (dealer) =>
                              dealer.name === dealerFilterDiurnoPichincha
                          );
                        }

                        const avgDistance =
                          calculatedDealers && calculatedDealers.length
                            ? calculatedDealers.reduce((sum, dealer) => {
                                const dist = dealer.avgDistance
                                  ? parseFloat(dealer.avgDistance) || 0
                                  : 0;
                                const vehicles = dealer.vehicles || 0;
                                return sum + dist * vehicles;
                              }, 0) /
                              calculatedDealers.reduce(
                                (sum, dealer) => sum + (dealer.vehicles || 0),
                                0
                              )
                            : 0;

                        return (
                        <tr key={parish}>
                          <td style={theme.table.cell}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {viewButton(() =>
                                  showVehicleModal(
                                    "Pichincha",
                                    canton,
                                    parishName,
                                    data,
                                    "diurno",
                                    dealerFilterDiurnoPichincha || null
                                  )
                                )}
                                <div>
                                  {parishName}
                            <div
                              style={{
                                fontSize: "12px",
                                color: theme.text.light,
                              }}
                            >
                                    {dealerFilterDiurnoPichincha
                                      ? formatNumber(
                                          data.dealers.find(
                                            (d) =>
                                              d.name ===
                                              dealerFilterDiurnoPichincha
                                          )?.vehicles || 0
                                        )
                                      : formatNumber(data.total_vehicles)}{" "}
                                    vehículos
                                  </div>
                                </div>
                            </div>
                          </td>
                          <td
                              style={{
                                ...theme.table.cell,
                                textAlign: "center",
                                fontWeight: "500",
                              }}
                            >
                              {avgDistance ? avgDistance.toFixed(2) : "N/A"} KM
                            </td>
                            <td
                              style={{
                                ...theme.table.cell,
                                padding: "16px 8px",
                              }}
                            >
                              <DealerChart
                                dealers={data.dealers}
                                selectedDealer={dealerFilterDiurnoPichincha}
                              />
                          </td>
                        </tr>
                        );
                      })}
                </tbody>
              </table>
            </div>
          </Card>
        </Col>
        <Col span={12}>
          <Card
            title={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <span
                  style={{ fontSize: "14px", textAlign: "center", flex: 1 }}
                >
                  PARROQUIAS DE PICHINCHA – LUGARES DE DOMICILIO (NOCTURNO)
                </span>
                {exportButton(() =>
                  handleDownloadParishDealers(
                    "Pichincha",
                    "nocturno",
                    dealerFilterNocturnoPichincha || null
                  )
                )}
              </div>
            }
            style={{
              ...theme.cards.base,
              height: "600px",
              display: "flex",
              flexDirection: "column",
            }}
            bodyStyle={{ padding: "16px", flex: 1, overflow: "hidden" }}
          >
            <DealerSelector
              dealers={getUniqueDealers(
                parishDealerStats.nocturno,
                "Pichincha"
              )}
              value={dealerFilterNocturnoPichincha}
              onChange={setDealerFilterNocturnoPichincha}
              placeholder="Filtrar por punto de venta"
            />
            <div style={{ height: "100%", overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...theme.table.header, width: "30%" }}>
                      PARROQUIA
                    </th>
                    <th style={{ ...theme.table.header, width: "15%" }}>
                      DIST. PROM. (KM)
                    </th>
                    <th style={{ ...theme.table.header, width: "55%" }}>
                      DISTRIBUCIÓN DE PUNTOS DE VENTA
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {parishDealerStats.nocturno &&
                    Object.entries(parishDealerStats.nocturno)
                      .filter(([parish]) => parish.includes("Pichincha"))
                      .filter(
                        ([_, data]) =>
                          // Si hay un filtro de dealer, verificar que al menos uno de los dealers coincida
                          !dealerFilterNocturnoPichincha ||
                          data.dealers.some(
                            (dealer) =>
                              dealer.name === dealerFilterNocturnoPichincha
                          )
                      )
                      .map(([parish, data]) => {
                        const [, parishName] = parish.split(" - ");
                        // Obtener el cantón al que pertenece esta parroquia
                        const cantonInfo = Object.entries(
                          parishStats.Pichincha
                        ).find(([canton, parishes]) =>
                          parishes.some((p) => p.day_parish === parishName)
                        );
                        const canton = cantonInfo ? cantonInfo[0] : "";

                        // Calcular la distancia promedio de los concesionarios
                        let calculatedDealers = data.dealers;
                        if (dealerFilterNocturnoPichincha) {
                          calculatedDealers = data.dealers.filter(
                            (dealer) =>
                              dealer.name === dealerFilterNocturnoPichincha
                          );
                        }

                        const avgDistance =
                          calculatedDealers && calculatedDealers.length
                            ? calculatedDealers.reduce((sum, dealer) => {
                                const dist = dealer.avgDistance
                                  ? parseFloat(dealer.avgDistance) || 0
                                  : 0;
                                const vehicles = dealer.vehicles || 0;
                                return sum + dist * vehicles;
                              }, 0) /
                              calculatedDealers.reduce(
                                (sum, dealer) => sum + (dealer.vehicles || 0),
                                0
                              )
                            : 0;

                        return (
                        <tr key={parish}>
                          <td style={theme.table.cell}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {viewButton(() =>
                                  showVehicleModal(
                                    "Pichincha",
                                    canton,
                                    parishName,
                                    data,
                                    "nocturno",
                                    dealerFilterNocturnoPichincha || null
                                  )
                                )}
                                <div>
                                  {parishName}
                            <div
                              style={{
                                fontSize: "12px",
                                color: theme.text.light,
                              }}
                            >
                                    {dealerFilterNocturnoPichincha
                                      ? formatNumber(
                                          data.dealers.find(
                                            (d) =>
                                              d.name ===
                                              dealerFilterNocturnoPichincha
                                          )?.vehicles || 0
                                        )
                                      : formatNumber(data.total_vehicles)}{" "}
                                    vehículos
                                  </div>
                                </div>
                            </div>
                          </td>
                          <td
                              style={{
                                ...theme.table.cell,
                                textAlign: "center",
                                fontWeight: "500",
                              }}
                            >
                              {avgDistance ? avgDistance.toFixed(2) : "N/A"} KM
                            </td>
                            <td
                              style={{
                                ...theme.table.cell,
                                padding: "16px 8px",
                              }}
                            >
                              <DealerChart
                                dealers={data.dealers}
                                selectedDealer={dealerFilterNocturnoPichincha}
                              />
                          </td>
                        </tr>
                        );
                      })}
                </tbody>
              </table>
            </div>
          </Card>
        </Col>

        {/* Listados para Guayas */}
        <Col span={12}>
          <Card
            title={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <span
                  style={{ fontSize: "14px", textAlign: "center", flex: 1 }}
                >
                  PARROQUIAS DE GUAYAS – LUGARES DE TRABAJO (DIURNO)
                </span>
                {exportButton(() =>
                  handleDownloadParishDealers(
                    "Guayas",
                    "diurno",
                    dealerFilterDiurnoGuayas || null
                  )
                )}
              </div>
            }
            style={{
              ...theme.cards.base,
              height: "600px",
              display: "flex",
              flexDirection: "column",
            }}
            bodyStyle={{ padding: "16px", flex: 1, overflow: "hidden" }}
          >
            <DealerSelector
              dealers={getUniqueDealers(parishDealerStats.diurno, "Guayas")}
              value={dealerFilterDiurnoGuayas}
              onChange={setDealerFilterDiurnoGuayas}
              placeholder="Filtrar por punto de venta"
            />
            <div style={{ height: "100%", overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...theme.table.header, width: "30%" }}>
                      PARROQUIA
                    </th>
                    <th style={{ ...theme.table.header, width: "15%" }}>
                      DIST. PROM. (KM)
                    </th>
                    <th style={{ ...theme.table.header, width: "55%" }}>
                      DISTRIBUCIÓN DE PUNTOS DE VENTA
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {parishDealerStats.diurno &&
                    Object.entries(parishDealerStats.diurno)
                      .filter(([parish]) => parish.includes("Guayas"))
                      .filter(
                        ([_, data]) =>
                          // Si hay un filtro de dealer, verificar que al menos uno de los dealers coincida
                          !dealerFilterDiurnoGuayas ||
                          data.dealers.some(
                            (dealer) => dealer.name === dealerFilterDiurnoGuayas
                          )
                      )
                      .map(([parish, data]) => {
                        const [, parishName] = parish.split(" - ");
                        // Obtener el cantón al que pertenece esta parroquia
                        const cantonInfo = Object.entries(
                          parishStats.Guayas
                        ).find(([canton, parishes]) =>
                          parishes.some((p) => p.day_parish === parishName)
                        );
                        const canton = cantonInfo ? cantonInfo[0] : "";

                        // Calcular la distancia promedio de los concesionarios
                        let calculatedDealers = data.dealers;
                        if (dealerFilterDiurnoGuayas) {
                          calculatedDealers = data.dealers.filter(
                            (dealer) => dealer.name === dealerFilterDiurnoGuayas
                          );
                        }

                        const avgDistance =
                          calculatedDealers && calculatedDealers.length
                            ? calculatedDealers.reduce((sum, dealer) => {
                                const dist = dealer.avgDistance
                                  ? parseFloat(dealer.avgDistance) || 0
                                  : 0;
                                const vehicles = dealer.vehicles || 0;
                                return sum + dist * vehicles;
                              }, 0) /
                              calculatedDealers.reduce(
                                (sum, dealer) => sum + (dealer.vehicles || 0),
                                0
                              )
                            : 0;

                        return (
                        <tr key={parish}>
                          <td style={theme.table.cell}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {viewButton(() =>
                                  showVehicleModal(
                                    "Guayas",
                                    canton,
                                    parishName,
                                    data,
                                    "diurno",
                                    dealerFilterDiurnoGuayas || null
                                  )
                                )}
                                <div>
                                  {parishName}
                            <div
                              style={{
                                fontSize: "12px",
                                color: theme.text.light,
                              }}
                            >
                                    {dealerFilterDiurnoGuayas
                                      ? formatNumber(
                                          data.dealers.find(
                                            (d) =>
                                              d.name ===
                                              dealerFilterDiurnoGuayas
                                          )?.vehicles || 0
                                        )
                                      : formatNumber(data.total_vehicles)}{" "}
                                    vehículos
                                  </div>
                                </div>
                            </div>
                          </td>
                          <td
                              style={{
                                ...theme.table.cell,
                                textAlign: "center",
                                fontWeight: "500",
                              }}
                            >
                              {avgDistance ? avgDistance.toFixed(2) : "N/A"} KM
                            </td>
                            <td
                              style={{
                                ...theme.table.cell,
                                padding: "16px 8px",
                              }}
                            >
                              <DealerChart
                                dealers={data.dealers}
                                selectedDealer={dealerFilterDiurnoGuayas}
                              />
                          </td>
                        </tr>
                        );
                      })}
                </tbody>
              </table>
            </div>
          </Card>
        </Col>
        <Col span={12}>
          <Card
            title={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <span
                  style={{ fontSize: "14px", textAlign: "center", flex: 1 }}
                >
                  PARROQUIAS DE GUAYAS – LUGARES DE DOMICILIO (NOCTURNO)
                </span>
                {exportButton(() =>
                  handleDownloadParishDealers(
                    "Guayas",
                    "nocturno",
                    dealerFilterNocturnoGuayas || null
                  )
                )}
              </div>
            }
            style={{
              ...theme.cards.base,
              height: "600px",
              display: "flex",
              flexDirection: "column",
            }}
            bodyStyle={{ padding: "16px", flex: 1, overflow: "hidden" }}
          >
            <DealerSelector
              dealers={getUniqueDealers(parishDealerStats.nocturno, "Guayas")}
              value={dealerFilterNocturnoGuayas}
              onChange={setDealerFilterNocturnoGuayas}
              placeholder="Filtrar por punto de venta"
            />
            <div style={{ height: "100%", overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...theme.table.header, width: "30%" }}>
                      PARROQUIA
                    </th>
                    <th style={{ ...theme.table.header, width: "15%" }}>
                      DIST. PROM. (KM)
                    </th>
                    <th style={{ ...theme.table.header, width: "55%" }}>
                      DISTRIBUCIÓN DE PUNTOS DE VENTA
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {parishDealerStats.nocturno &&
                    Object.entries(parishDealerStats.nocturno)
                      .filter(([parish]) => parish.includes("Guayas"))
                      .filter(
                        ([_, data]) =>
                          // Si hay un filtro de dealer, verificar que al menos uno de los dealers coincida
                          !dealerFilterNocturnoGuayas ||
                          data.dealers.some(
                            (dealer) =>
                              dealer.name === dealerFilterNocturnoGuayas
                          )
                      )
                      .map(([parish, data]) => {
                        const [, parishName] = parish.split(" - ");
                        // Obtener el cantón al que pertenece esta parroquia
                        const cantonInfo = Object.entries(
                          parishStats.Guayas
                        ).find(([canton, parishes]) =>
                          parishes.some((p) => p.day_parish === parishName)
                        );
                        const canton = cantonInfo ? cantonInfo[0] : "";

                        // Calcular la distancia promedio de los concesionarios
                        let calculatedDealers = data.dealers;
                        if (dealerFilterNocturnoGuayas) {
                          calculatedDealers = data.dealers.filter(
                            (dealer) =>
                              dealer.name === dealerFilterNocturnoGuayas
                          );
                        }

                        const avgDistance =
                          calculatedDealers && calculatedDealers.length
                            ? calculatedDealers.reduce((sum, dealer) => {
                                const dist = dealer.avgDistance
                                  ? parseFloat(dealer.avgDistance) || 0
                                  : 0;
                                const vehicles = dealer.vehicles || 0;
                                return sum + dist * vehicles;
                              }, 0) /
                              calculatedDealers.reduce(
                                (sum, dealer) => sum + (dealer.vehicles || 0),
                                0
                              )
                            : 0;

                        return (
                        <tr key={parish}>
                          <td style={theme.table.cell}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {viewButton(() =>
                                  showVehicleModal(
                                    "Guayas",
                                    canton,
                                    parishName,
                                    data,
                                    "nocturno",
                                    dealerFilterNocturnoGuayas || null
                                  )
                                )}
                                <div>
                                  {parishName}
                            <div
                              style={{
                                fontSize: "12px",
                                color: theme.text.light,
                              }}
                            >
                                    {dealerFilterNocturnoGuayas
                                      ? formatNumber(
                                          data.dealers.find(
                                            (d) =>
                                              d.name ===
                                              dealerFilterNocturnoGuayas
                                          )?.vehicles || 0
                                        )
                                      : formatNumber(data.total_vehicles)}{" "}
                                    vehículos
                                  </div>
                                </div>
                            </div>
                          </td>
                          <td
                              style={{
                                ...theme.table.cell,
                                textAlign: "center",
                                fontWeight: "500",
                              }}
                            >
                              {avgDistance ? avgDistance.toFixed(2) : "N/A"} KM
                            </td>
                            <td
                              style={{
                                ...theme.table.cell,
                                padding: "16px 8px",
                              }}
                            >
                              <DealerChart
                                dealers={data.dealers}
                                selectedDealer={dealerFilterNocturnoGuayas}
                              />
                          </td>
                        </tr>
                        );
                      })}
                </tbody>
              </table>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Modal para mostrar los vehículos */}
      <Modal
        title={modalTitle}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={
          vehicleData.length > 0
            ? [
                <Button
                  key="download"
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleExportVehiclesToExcel}
                  style={{
                    backgroundColor: theme.colors.success,
                    borderColor: theme.colors.success,
                  }}
                >
                  Exportar a Excel
                </Button>,
                <Button key="close" onClick={() => setModalVisible(false)}>
                  Cerrar
                </Button>,
              ]
            : null
        }
        width="90%"
        style={{ top: 20 }}
      >
        {loadingVehicles ? (
          <div style={{ textAlign: "center", padding: "30px" }}>
            <Spin size="large" />
            <p style={{ marginTop: "16px" }}>
              Cargando vehículos de la parroquia...
            </p>
          </div>
        ) : vehicleData.length === 0 ? (
          <div style={{ textAlign: "center", padding: "30px", color: "#999" }}>
            <p>
              No se encontraron vehículos para esta parroquia en el período{" "}
              {timeType === "diurno" ? "diurno" : "nocturno"}.
            </p>
            <p>
              Es posible que no haya datos registrados o que la parroquia no
              tenga vehículos asignados en este período.
            </p>
          </div>
        ) : (
          <>
            <div style={{ margin: "0 0 16px 0", padding: "0 8px" }}>
              <Typography.Text strong>
                Total de vehículos encontrados: {vehicleData.length}
              </Typography.Text>
            </div>
            <Table
              dataSource={vehicleData}
              columns={vehicleColumns}
              rowKey="vin"
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                locale: {
                  items_per_page: "/ página",
                },
              }}
              scroll={{ y: 500 }}
              bordered
              size="middle"
              rowClassName={(record, index) =>
                index % 2 === 0 ? "" : "ant-table-row-light"
              }
              locale={{
                emptyText: "No hay datos disponibles",
                filterConfirm: "Aceptar",
                filterReset: "Restablecer",
                filterTitle: "Filtrar",
                filterEmptyText: "Sin filtros",
                selectAll: "Seleccionar página actual",
                selectInvert: "Invertir selección",
                selectionAll: "Seleccionar todo",
                sortTitle: "Ordenar",
                triggerDesc: "Clic para ordenar descendente",
                triggerAsc: "Clic para ordenar ascendente",
                cancelSort: "Clic para cancelar ordenamiento",
              }}
            />
          </>
        )}
      </Modal>
    </>
  );
};

export default SummaryTables;
